import type { TagCategory, TagOption } from "~/ui-rtk/types/structured-tags"
import { emptySplitApi } from "./emptyApi"

interface CreateTagOptionRequest {
  slug: string
  body: {
    label: string
    value?: string
    color?: string
    productId?: string
  }
}

export interface CreateTagCategoryInput {
  label: string
  slug?: string
  description?: string
  valueType?: "predefined" | "user_generated" | "mixed" | "free_text"
  allowMultiple?: boolean
  options?: Array<{ label: string; value?: string; color?: string }>
}

export interface CreateTagCategoriesResponse {
  created: TagCategory[]
  failed: Array<{ slug: string; reason: string }>
}

/** Input: { categorySlug: [{ label, value?, color? }] } */
type BulkCreateOptionsRequest = Record<
  string,
  Array<{ label: string; value?: string; color?: string }>
>

/** Output: { categorySlug: TagOption[] } */
type BulkCreateOptionsResponse = Record<string, TagOption[]>

export type TaxonomyImportAction = "create" | "update" | "unchanged"

export interface TaxonomyImportOptionPlan {
  label: string
  value: string
  action: TaxonomyImportAction
  /** Fields that change on an update: "label", "description". */
  changes: string[]
}

export interface TaxonomyImportCategoryPlan {
  slug: string
  label: string
  action: TaxonomyImportAction
  changes: string[]
  isSystem: boolean
  valueType: string
  options: TaxonomyImportOptionPlan[]
}

export interface TaxonomyImportSummary {
  categoriesCreated: number
  categoriesUpdated: number
  categoriesUnchanged: number
  optionsCreated: number
  optionsUpdated: number
  optionsUnchanged: number
}

/** POST /tag-categories/import — the plan (dryRun) or what was applied. */
export interface TaxonomyImportResult {
  dryRun: boolean
  categories: TaxonomyImportCategoryPlan[]
  summary: TaxonomyImportSummary
  warnings: string[]
}

/** How much of the ad library carries each category's tags. */
export type TagCoveragePlatform = {
  platform: string
  totalAds: number
  taggedAds: number
}

export type TagCoverageCategory = {
  slug: string
  label: string
  totalAds: number
  taggedAds: number
  byPlatform: TagCoveragePlatform[]
}

export const tagCategoriesApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getTagCoverage: builder.query<TagCoverageCategory[], void>({
      query: () => "tag-categories/coverage",
      // Option-level mutations invalidate the shared LIST tag, refreshing a
      // mounted Coverage tab. Category-level mutations patch the categories
      // cache directly (no invalidation), and tag assignments change outside
      // this API entirely — so CoverageTab also refetches on every mount
      // rather than relying on this tag alone.
      providesTags: [{ type: "TagCategory", id: "LIST" }],
    }),

    getTagCategories: builder.query<TagCategory[], void>({
      query: () => "tag-categories",
      providesTags: result =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "TagCategory" as const,
                id,
              })),
              { type: "TagCategory", id: "LIST" },
            ]
          : [{ type: "TagCategory", id: "LIST" }],
    }),

    createTagCategories: builder.mutation<
      CreateTagCategoriesResponse,
      CreateTagCategoryInput[]
    >({
      query: categories => ({
        url: "tag-categories",
        method: "POST",
        body: { categories },
      }),
      // No invalidation — append created categories to the cached list directly.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(
            tagCategoriesApi.util.updateQueryData(
              "getTagCategories",
              undefined,
              draft => {
                for (const c of data.created) {
                  if (!draft.find(d => d.id === c.id)) draft.push(c)
                }
                draft.sort((a, b) => a.sortOrder - b.sortOrder)
              },
            ),
          )
        } catch {
          // Mutation rejected — leave cache untouched.
        }
      },
    }),

    /**
     * Upsert the taxonomy from a spreadsheet export (Category, Category
     * Definition, tag value, tag value Definition). Missing categories and
     * values are created, existing ones get the sheet's label/description,
     * nothing is deleted. `dryRun: true` returns the plan for a preview and
     * leaves the cache alone; a real run refetches the category list.
     */
    importTagTaxonomy: builder.mutation<
      TaxonomyImportResult,
      { csv: string; dryRun?: boolean }
    >({
      query: body => ({
        url: "tag-categories/import",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { dryRun }) =>
        dryRun ? [] : [{ type: "TagCategory", id: "LIST" }],
    }),

    renameTagCategory: builder.mutation<
      TagCategory,
      { slug: string; label: string }
    >({
      query: ({ slug, label }) => ({
        url: `tag-categories/${slug}`,
        method: "PATCH",
        body: { label },
      }),
      // Optimistically update the cached label; roll back on failure.
      async onQueryStarted({ slug, label }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          tagCategoriesApi.util.updateQueryData(
            "getTagCategories",
            undefined,
            draft => {
              const cat = draft.find(c => c.slug === slug)
              if (cat) cat.label = label
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    /**
     * Partial category update — label and/or description. Descriptions guide
     * the AI tag suggester, so they're editable on system categories too.
     */
    updateTagCategory: builder.mutation<
      TagCategory,
      { slug: string; body: { label?: string; description?: string } }
    >({
      query: ({ slug, body }) => ({
        url: `tag-categories/${slug}`,
        method: "PATCH",
        body,
      }),
      async onQueryStarted({ slug, body }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          tagCategoriesApi.util.updateQueryData(
            "getTagCategories",
            undefined,
            draft => {
              const cat = draft.find(c => c.slug === slug)
              if (!cat) return
              if (body.label !== undefined) cat.label = body.label
              if (body.description !== undefined) {
                cat.description = body.description || undefined
              }
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    /**
     * Update one option's label/description/color. Invalidates rather than
     * patching: updating a `virtual:` option materializes it server-side,
     * changing its id.
     */
    updateTagOption: builder.mutation<
      TagOption,
      {
        slug: string
        optionId: string
        body: { label?: string; description?: string; color?: string }
      }
    >({
      query: ({ slug, optionId, body }) => ({
        url: `tag-categories/${slug}/options/${optionId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [{ type: "TagCategory", id: "LIST" }],
    }),

    deleteTagCategory: builder.mutation<void, { slug: string }>({
      query: ({ slug }) => ({
        url: `tag-categories/${slug}`,
        method: "DELETE",
      }),
      async onQueryStarted({ slug }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          tagCategoriesApi.util.updateQueryData(
            "getTagCategories",
            undefined,
            draft => {
              const idx = draft.findIndex(c => c.slug === slug)
              if (idx !== -1) draft.splice(idx, 1)
            },
          ),
        )
        try {
          await queryFulfilled
        } catch {
          patch.undo()
        }
      },
    }),

    createTagOption: builder.mutation<TagOption, CreateTagOptionRequest>({
      query: ({ slug, body }) => ({
        url: `tag-categories/${slug}/options`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "TagCategory", id: "LIST" }],
    }),

    bulkCreateOptions: builder.mutation<
      BulkCreateOptionsResponse,
      BulkCreateOptionsRequest
    >({
      query: categoriesInput => {
        // Convert Record<slug, options[]> to entries array for validated DTO
        const entries = Object.entries(categoriesInput).map(
          ([slug, options]) => ({ slug, options }),
        )
        return {
          url: "tag-categories/bulk-options",
          method: "POST",
          body: { entries },
        }
      },
      invalidatesTags: [{ type: "TagCategory", id: "LIST" }],
    }),

    deleteTagOption: builder.mutation<void, { slug: string; optionId: string }>(
      {
        query: ({ slug, optionId }) => ({
          url: `tag-categories/${slug}/options/${optionId}`,
          method: "DELETE",
        }),
        invalidatesTags: [{ type: "TagCategory", id: "LIST" }],
      },
    ),
  }),
})

export const {
  useGetTagCategoriesQuery,
  useGetTagCoverageQuery,
  useCreateTagCategoriesMutation,
  useImportTagTaxonomyMutation,
  useRenameTagCategoryMutation,
  useUpdateTagCategoryMutation,
  useUpdateTagOptionMutation,
  useDeleteTagCategoryMutation,
  useCreateTagOptionMutation,
  useBulkCreateOptionsMutation,
  useDeleteTagOptionMutation,
} = tagCategoriesApi
